import { PackageData } from "@/pages/CreatePackage";

export interface PriceBreakdown {
  hotelsTotal: number;
  flightsTotal: number;
  transportTotal: number;
  toursTotal: number;
  servicesTotal: number;
  basePrice: number;
  profitAmount: number;
  discount: number;
  taxes: number;
  totalPrice: number;
}

/**
 * حساب إجمالي أسعار الفنادق
 */
export const calculateHotelsTotal = (hotels: PackageData['hotels']): number => {
  if (!hotels || hotels.length === 0) return 0;
  return hotels.reduce((total, hotel) => {
    const nights = hotel.nights || 1;
    const pricePerNight = hotel.pricePerNight || 0;
    return total + (pricePerNight * nights);
  }, 0);
};

/**
 * حساب إجمالي أسعار الطيران
 */
export const calculateFlightsTotal = (flights: PackageData['flights']): number => {
  if (!flights || flights.length === 0) return 0;
  return flights.reduce((total, flight) => {
    const price = flight.sellingPrice || flight.price || 0;
    return total + price;
  }, 0);
};

/**
 * حساب إجمالي أسعار المواصلات
 */
export const calculateTransportTotal = (transport: PackageData['transport']): number => {
  if (!transport || transport.length === 0) return 0;
  return transport.reduce((total, item) => {
    return total + (item.price || 0);
  }, 0);
};

/**
 * حساب إجمالي أسعار الجولات
 */
export const calculateToursTotal = (tours: PackageData['tours']): number => {
  if (!tours || tours.length === 0) return 0;
  return tours.reduce((total, tour) => {
    return total + (tour.price || 0);
  }, 0);
};

/**
 * حساب إجمالي أسعار الخدمات
 */
export const calculateServicesTotal = (services: PackageData['services']): number => {
  if (!services || services.length === 0) return 0;
  return services.reduce((total, service) => {
    return total + (service.price || 0);
  }, 0);
};

/**
 * حساب السعر الأساسي (مجموع كل الخدمات)
 */
export const calculateBasePrice = (data: PackageData): number => {
  const hotelsTotal = data.includesHotels ? calculateHotelsTotal(data.hotels) : 0;
  const flightsTotal = (data.includesInternationalFlights || data.includesDomesticFlights) 
    ? calculateFlightsTotal(data.flights) 
    : 0;
  const transportTotal = data.includesTransport ? calculateTransportTotal(data.transport) : 0;
  const toursTotal = data.includesTours ? calculateToursTotal(data.tours) : 0;
  const servicesTotal = data.includesServices ? calculateServicesTotal(data.services) : 0;
  
  return hotelsTotal + flightsTotal + transportTotal + toursTotal + servicesTotal;
};

/**
 * حساب الربح
 */
export const calculateProfitAmount = (
  basePrice: number,
  profitType: 'percentage' | 'fixed',
  profitValue: number
): number => {
  if (profitType === 'percentage') {
    return basePrice * (profitValue / 100);
  }
  return profitValue;
};

/**
 * حساب السعر النهائي
 */
export const calculateTotalPrice = (
  basePrice: number,
  profitAmount: number,
  discount: number = 0,
  taxes: number = 0
): number => {
  const total = basePrice + profitAmount - discount + taxes;
  return Math.max(0, total);
};

/**
 * حساب كامل تفاصيل السعر
 */
export const calculateFullPriceBreakdown = (data: PackageData): PriceBreakdown => {
  const hotelsTotal = data.includesHotels ? calculateHotelsTotal(data.hotels) : 0;
  const flightsTotal = (data.includesInternationalFlights || data.includesDomesticFlights) 
    ? calculateFlightsTotal(data.flights) 
    : 0;
  const transportTotal = data.includesTransport ? calculateTransportTotal(data.transport) : 0;
  const toursTotal = data.includesTours ? calculateToursTotal(data.tours) : 0;
  const servicesTotal = data.includesServices ? calculateServicesTotal(data.services) : 0;
  
  const basePrice = hotelsTotal + flightsTotal + transportTotal + toursTotal + servicesTotal;
  
  const profitAmount = calculateProfitAmount(
    basePrice,
    data.profitType || 'percentage',
    data.profitValue || 20
  );
  
  const discount = data.discount || 0;
  const taxes = data.taxes || 0;
  
  const totalPrice = calculateTotalPrice(basePrice, profitAmount, discount, taxes);
  
  return {
    hotelsTotal,
    flightsTotal,
    transportTotal,
    toursTotal,
    servicesTotal,
    basePrice,
    profitAmount,
    discount,
    taxes,
    totalPrice,
  };
};

/**
 * حساب عدد الليالي بناءً على تاريخي الدخول والخروج
 */
export const calculateNights = (checkIn: string, checkOut: string): number => {
  if (!checkIn || !checkOut) return 1;
  
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  
  const diffTime = Math.abs(checkOutDate.getTime() - checkInDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(1, diffDays);
};

/**
 * حساب السعر لكل شخص (متوسط)
 */
export const calculatePricePerPerson = (totalPrice: number, data: PackageData): number => {
  const totalPersons = (data.adultsCount || 1) + (data.childrenCount || 0);
  return totalPersons > 0 ? totalPrice / totalPersons : totalPrice;
};

/**
 * حساب نسبة الربح من السعر النهائي
 */
export const calculateProfitPercentage = (profitAmount: number, totalPrice: number): number => {
  if (totalPrice === 0) return 0;
  return (profitAmount / totalPrice) * 100;
};
